"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.IssueCreationForm = void 0;
var _react = _interopRequireDefault(require("react"));
var _formik = require("formik");
var _alloyReactTextarea = _interopRequireDefault(require("@adsk/alloy-react-textarea"));
var _alloyReactTextInput = _interopRequireDefault(require("@adsk/alloy-react-text-input"));
var _sdk = require("../../sdk");
var _FieldWrapper = require("../input-field-components/FieldWrapper");
var _StatusDropdown = require("../input-field-components/StatusDropdown");
var _TypeDropdown = require("../input-field-components/TypeDropdown");
var _RootCauseDropdown = require("../input-field-components/RootCauseDropdown");
var _ProjectMemberDropdown = require("../input-field-components/ProjectMemberDropdown");
var _DateField = require("../input-field-components/DateField");
var _CustomFieldWithFormikContext = require("../input-field-components/CustomFieldWithFormikContext");
var _common = require("../input-field-components/ProjectMemberDropdown/common");
var _PlacementDropdown = _interopRequireDefault(require("../input-field-components/PlacementDropdown/PlacementDropdown"));
var _IssueTemplatesField = require("./IssueTemplatesField");
var _FixedTypeField = require("./FixedTypeField");
var _LocationCreateField = require("./LocationCreateField");
var _useLocalStorage = require("../hooks/useLocalStorage");
var _hooks = require("../state/redux/hooks");
var _currentUserPermissions = require("../state/redux/ducks/currentUserPermissions");
var _issueTypes = require("../state/redux/ducks/issueTypes");
var _ConditionalTooltip = require("../utils/components/ConditionalTooltip");
var _useHydratedRef = require("../utils/hooks/useHydratedRef");
var _issueCustomFieldMappingHooks = require("../utils/hooks/issueCustomFieldMappingHooks");
var _useScrollToFirstErrorOnSubmit = require("../utils/hooks/useScrollToFirstErrorOnSubmit");
var _useTranslations = require("../utils/i18n/useTranslations");
var _requiredFieldsByTypeHooks = require("../utils/hooks/requiredFieldsByTypeHooks");
var _issueFieldOrdering = require("../utils/data/issueFieldOrdering");
var _useIncrementor = require("../utils/hooks/useIncrementor");
var _featureFlags = require("../utils/featureFlags");
var _GeoLocationPickerField = require("../input-field-components/GeoLocationPickerField");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const IssueCreationForm = _ref => {
  let {
    titleFieldLabel,
    requireTitleField = true,
    hideStatusField,
    hiddenStandardFields,
    fixedTypeId = undefined
  } = _ref;
  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    setFieldTouched,
    touched,
    errors,
    submitCount
  } = (0, _formik.useFormikContext)();
  const typeEntities = (0, _hooks.useSelector)(_issueTypes.getIssueTypeEntitiesMap);
  const selectedType = typeEntities === null || typeEntities === void 0 ? void 0 : typeEntities[values.issueTypeId];
  const {
    mappingsByCustomFieldId,
    customFieldIds
  } = (0, _issueCustomFieldMappingHooks.useCustomFieldIdsWithMappingsForType)(values.issueTypeId);
  const issueFields = (0, _issueFieldOrdering.useGetSanitizedFieldOrder)({
    fieldList: (selectedType === null || selectedType === void 0 ? void 0 : selectedType.fieldsMetadata) || [],
    customFieldIdsForType: customFieldIds
  })
  // Hide any fields that the consumer has explicitly requested to be hidden
  .map(field => hiddenStandardFields !== null && hiddenStandardFields !== void 0 && hiddenStandardFields.some(hiddenField => hiddenField === field.id) ? Object.assign({
    ...field
  }, {
    visible: false
  }) : field);
  const requiredStandardFieldIds = ((selectedType === null || selectedType === void 0 ? void 0 : selectedType.fieldsMetadata) || []).filter(_ref2 => {
    let {
      required,
      type
    } = _ref2;
    return type === "standard" && !!required;
  }).map(_ref3 => {
    let {
      id
    } = _ref3;
    return id;
  });
  const areThereHiddenRequiredStandardFields = Array.isArray(hiddenStandardFields) && hiddenStandardFields.some(f => {
    return requiredStandardFieldIds.includes(f);
  });
  (0, _requiredFieldsByTypeHooks.useChangeStatusWhenRequiredFieldsAreHidden)({
    hideStatusField,
    areThereHiddenRequiredStandardFields,
    issueFormValues: values,
    selectedType,
    setFieldValue
  });
  const [, setLastSelectedTypeId] = (0, _useLocalStorage.useLocalStorage)(_useLocalStorage.LocalStorageKeys.LAST_SELECTED_TYPE_ID);
  const permittedStatuses = (0, _hooks.useSelector)(_currentUserPermissions.getCurrentUserPermittedStatusesForNewIssue);
  const permittedActions = (0, _hooks.useSelector)(_currentUserPermissions.getCurrentUserPermittedActionsForNewIssue);
  const levelOneUserShouldIncludeMemberFn = (0, _common.useGetLevelOneUserShouldIncludeMemberFn)();
  const shouldIncludeMemberFn = !permittedActions.includes(_sdk.PermittedAction.AssignAll) ? levelOneUserShouldIncludeMemberFn : undefined;
  const ref = (0, _useHydratedRef.useHydratedRef)();
  (0, _useScrollToFirstErrorOnSubmit.useScrollToFirstErrorOnSubmit)(ref);
  const {
    T
  } = (0, _useTranslations.useTranslations)();
  const [countKey, incrementCountKey] = (0, _useIncrementor.useIncrementor)();
  const {
    value: shouldHideEmptyRolesAndCompanies
  } = (0, _featureFlags.useFeatureFlag)(_featureFlags.FeatureFlag.HideEmptyRolesAndCompanies);
  return (
    /*#__PURE__*/
    // The ref had to be moved to an upper div because it was having some race condition when the
    // template was selected. Some times it would work as expected but others it would cause errors
    // on components with dropdowns that relied on the ref to work. This way we will always have the
    // correct ref and only the child div will re-render when a template is selected
    _react.default.createElement("div", {
      ref: ref
    }, /*#__PURE__*/_react.default.createElement("div", {
      key: countKey
    }, /*#__PURE__*/_react.default.createElement(_formik.Form, null, !fixedTypeId && /*#__PURE__*/_react.default.createElement(_IssueTemplatesField.IssueTemplatesField
    // IssueTemplatesField handles everything about updating formik, and any confirmation
    // modals -- but we need the onTemplateChanged to ensure that the form values
    // in the current form are cleared, and we do that by re-rendering everything by
    // reinstantiating the form by updating the key of its wrapper.
    , {
      onTemplateChanged: () => {
        incrementCountKey();
      }
    }), /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
      required: requireTitleField,
      label: titleFieldLabel || T.translate("issueAttributes.title"),
      errorMessage: touched.title ? errors.title : undefined
    }, /*#__PURE__*/_react.default.createElement(_alloyReactTextInput.default, {
      name: "title",
      autoComplete: "off",
      value: values.title,
      onChange: handleChange,
      onBlur: handleBlur,
      placeholder: T.translate("issueCreation.title.placeholder"),
      error: touched.title && !!errors.title,
      "data-testid": "create-issue-modal-title"
    })), !hideStatusField && /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
      label: T.translate("issueAttributes.status"),
      errorMessage: touched.status ? errors.status : undefined
    }, /*#__PURE__*/_react.default.createElement(_ConditionalTooltip.ConditionalTooltip, {
      condition: areThereHiddenRequiredStandardFields,
      content: T.translate("issueCreation.status.requiredFieldDisabledReason")
    }, /*#__PURE__*/_react.default.createElement(_StatusDropdown.StatusDropdown, {
      status: values.status,
      statusOptions: permittedStatuses,
      onChange: newStatus => {
        const fieldName = "status";
        setFieldValue(fieldName, newStatus);
        setFieldTouched(fieldName, true, false);
      },
      "data-testid": "create-issue-modal-status",
      disabled: areThereHiddenRequiredStandardFields
    }))), fixedTypeId ? /*#__PURE__*/_react.default.createElement(_FixedTypeField.FixedTypeField, {
      typeId: fixedTypeId
    }) : /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
      label: T.translate("issueAttributes.issueType"),
      errorMessage: touched.issueTypeId ? errors.issueTypeId : undefined
    }, /*#__PURE__*/_react.default.createElement(_TypeDropdown.TypeDropdown, {
      selectedTypeId: values.issueTypeId,
      onChange: typeId => {
        const fieldName = "issueTypeId";
        setFieldValue(fieldName, typeId);
        setFieldTouched(fieldName, true, false);
        setLastSelectedTypeId(typeId);
      },
      "data-testid": "create-issue-modal-type"
    })), issueFields.map(field => {
      const fieldId = field.id;
      const isFieldRequired = !!field.required;
      if (!field.visible) {
        // do not show fields that should not be visible!
        return null;
      }
      if (fieldId === _sdk.IssueStandardFieldIds.Description) {
        return /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
          key: fieldId,
          label: T.translate("issueAttributes.description"),
          errorMessage: touched.description || submitCount > 0 ? errors.description : undefined,
          required: isFieldRequired
        }, /*#__PURE__*/_react.default.createElement(_alloyReactTextarea.default, {
          style: {
            width: "100%"
          }
          // @ts-ignore https://jira.autodesk.com/browse/DSGN-661
          ,
          name: "description",
          autoComplete: "off",
          value: values.description,
          onChange: handleChange,
          onBlur: handleBlur,
          maxHeight: 70,
          minHeight: 70,
          placeholder: T.translate("issueCreation.description.placeholder"),
          error: (touched.description || submitCount > 0) && !!errors.description,
          "data-testid": "create-issue-modal-description"
        }));
      } else if (fieldId === _sdk.IssueStandardFieldIds.Assignee) {
        return /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
          key: fieldId,
          label: T.translate("issueAttributes.assignedTo"),
          errorMessage: touched.assignedTo || submitCount > 0 ? errors.assignedTo : undefined,
          required: isFieldRequired
        }, /*#__PURE__*/_react.default.createElement(_ProjectMemberDropdown.ProjectMemberDropdown, {
          shouldIncludeMemberFn: shouldIncludeMemberFn,
          container: ref.current,
          selectedOxygenId: values.assignedTo,
          onChange: _ref4 => {
            var _values$watchers;
            let {
              value: oxygenId,
              memberType
            } = _ref4;
            const newValues = {
              ...values,
              assignedTo: oxygenId,
              assignedToType: memberType
            };
            if (memberType === "user" && !!oxygenId && !((_values$watchers = values.watchers) !== null && _values$watchers !== void 0 && _values$watchers.includes(oxygenId))) {
              newValues["watchers"] = [...(values.watchers || []), oxygenId];
            }
            setValues(newValues);
          },
          clearable: permittedActions.includes(_sdk.PermittedAction.ClearAssignee),
          "data-testid": "create-issue-modal-assignee",
          invalid: (touched.assignedTo || submitCount > 0) && !!errors.assignedTo,
          shouldIncludeEmpty: !shouldHideEmptyRolesAndCompanies
        }));
      } else if (fieldId === _sdk.IssueStandardFieldIds.Watchers) {
        return /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
          key: fieldId,
          label: /*#__PURE__*/_react.default.createElement(_ProjectMemberDropdown.WatcherFieldLabel, null),
          errorMessage: touched.watchers || submitCount > 0 ? errors.watchers : undefined,
          required: isFieldRequired
        }, /*#__PURE__*/_react.default.createElement(_ProjectMemberDropdown.WatcherDropdown, {
          shouldIncludeMemberFn: shouldIncludeMemberFn,
          selectedWatcherIds: values.watchers,
          onChange: selectedWatcherIds => {
            setFieldValue("watchers", selectedWatcherIds);
          },
          "data-testid": "create-issue-modal-watchers",
          invalid: (touched.watchers || submitCount > 0) && !!errors.watchers
        }));
      } else if (fieldId === _sdk.IssueStandardFieldIds.Location) {
        return /*#__PURE__*/_react.default.createElement(_LocationCreateField.LocationCreateField, {
          key: fieldId,
          isRequired: isFieldRequired,
          errorMessage: touched.locationId || submitCount > 0 ? errors.locationId : undefined
        });
      } else if (fieldId === _sdk.IssueStandardFieldIds.LocationDetails) {
        return /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
          key: fieldId,
          label: T.translate("issueAttributes.locationDetails"),
          errorMessage: touched.locationDetails || submitCount > 0 ? errors.locationDetails : undefined,
          required: isFieldRequired
        }, /*#__PURE__*/_react.default.createElement(_alloyReactTextInput.default, {
          name: "locationDetails",
          autoComplete: "off",
          value: values.locationDetails,
          onChange: handleChange,
          onBlur: handleBlur,
          placeholder: T.translate("issueCreation.locationDetails.placeholder"),
          error: (touched.locationDetails || submitCount > 0) && !!errors.locationDetails,
          "data-testid": "create-issue-modal-locationdetails"
        }));
      } else if (fieldId === _sdk.IssueStandardFieldIds.GpsCoordinates) {
        return /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
          key: fieldId,
          label: T.translate("issueAttributes.gpsCoordinates"),
          errorMessage: touched.gpsCoordinates || submitCount > 0 ? errors.gpsCoordinates : undefined,
          required: isFieldRequired
        }, /*#__PURE__*/_react.default.createElement(_GeoLocationPickerField.GeoLocationPickerField, {
          onChange: gpsCoordinates => {
            setFieldValue("gpsCoordinates", gpsCoordinates);
          },
          error: (touched.gpsCoordinates || submitCount > 0) && !!errors.gpsCoordinates,
          htmlId: fieldId
        }));
      } else if (fieldId === _sdk.IssueStandardFieldIds.DueDate) {
        return /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
          key: fieldId,
          label: T.translate("issueAttributes.dueDate"),
          errorMessage: touched.dueDate || submitCount > 0 ? errors.dueDate : undefined,
          required: isFieldRequired
        }, /*#__PURE__*/_react.default.createElement(_DateField.DateField, {
          date: values.dueDate,
          onChange: newDate => setFieldValue("dueDate", newDate),
          "data-testid": "create-issue-modal-duedate",
          error: (touched.dueDate || submitCount > 0) && !!errors.dueDate
        }));
      } else if (fieldId === _sdk.IssueStandardFieldIds.StartDate) {
        return /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
          key: fieldId,
          label: T.translate("issueAttributes.startDate"),
          errorMessage: touched.startDate || submitCount > 0 ? errors.startDate : undefined,
          required: isFieldRequired
        }, /*#__PURE__*/_react.default.createElement(_DateField.DateField, {
          date: values.startDate,
          onChange: newDate => setFieldValue("startDate", newDate),
          "data-testid": "create-issue-modal-startdate",
          error: (touched.startDate || submitCount > 0) && !!errors.startDate
        }));
      } else if (fieldId === _sdk.IssueStandardFieldIds.Placement) {
        return /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
          key: fieldId,
          label: T.translate("issueAttributes.placement")
        }, /*#__PURE__*/_react.default.createElement(_PlacementDropdown.default, {
          onChange: (placement, option) => {
            const {
              snapshotBlob,
              locationId
            } = option;
            if (!placement) {
              setFieldValue("linkedDocuments", undefined);
              setFieldValue("placement", undefined);
              setFieldValue("snapshotBlob", undefined);
              return;
            }
            if (Array.isArray(placement)) {
              setFieldValue("linkedDocuments", placement);
              if (snapshotBlob) {
                setFieldValue("snapshotBlob", snapshotBlob);
              }
            } else {
              if (locationId) {
                setFieldValue("locationId", locationId);
              }
              setFieldValue("placement", placement);
            }
          },
          error: !!errors.placement,
          issueType: selectedType
        }));
      } else if (fieldId === _sdk.IssueStandardFieldIds.RootCauseWithCategory) {
        return /*#__PURE__*/_react.default.createElement(_FieldWrapper.FieldWrapper, {
          key: fieldId,
          label: T.translate("issueAttributes.rootCause"),
          errorMessage: touched.rootCauseId || submitCount > 0 ? errors.rootCauseId : undefined,
          required: isFieldRequired
        }, /*#__PURE__*/_react.default.createElement(_RootCauseDropdown.RootCauseDropdown, {
          selectedRootCauseId: values.rootCauseId,
          onChange: _ref5 => {
            let {
              rootCauseId
            } = _ref5;
            return setFieldValue("rootCauseId", rootCauseId);
          },
          container: ref.current,
          "data-testid": "create-issue-modal-rootcause",
          invalid: (touched.rootCauseId || submitCount > 0) && !!errors.rootCauseId
        }));
      } else if (fieldId === _sdk.IssueStandardFieldIds.References) {
        // no references field yet in creation modal
        return null;
      } else if (field.type === "custom" && mappingsByCustomFieldId[field.id]) {
        const mapping = mappingsByCustomFieldId[field.id];
        return /*#__PURE__*/_react.default.createElement(_CustomFieldWithFormikContext.CustomFieldWithFormikContext, _extends({}, mapping, {
          key: fieldId,
          container: ref.current,
          isRequired: isFieldRequired
        }));
      }
    }))))
  );
};
exports.IssueCreationForm = IssueCreationForm;
//# sourceMappingURL=IssueCreationForm.js.map